import { captureException, captureMessage, withScope } from "@sentry/browser"
import { getAuth } from "firebase/auth"
import { set } from "firebase/database"
import { jwtDecode } from "jwt-decode"
import { useEffect, useMemo, useRef } from "react"
import { useFirebaseContext, useFirebaseValue } from "@pathwright/utils"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import { Clip } from "../types"

interface FirebaseConfig {
  // Future Firebase-specific options
}

/**
 * Hook-based Firebase backend for clipboard operations
 * Provides Firebase-based storage for authenticated users
 */
export const useFirebaseBackend = (config: FirebaseConfig = {}) => {
  try {
    const { me } = usePathwrightContext()
    const { app } = useFirebaseContext()

    const firebasePath = me ? `/user/${me.id}/clipboard/` : null
    // The clipboardRef will be null if the user is not authenticated or
    // or if Firebase couldn't initialize (ex: Firebase is down)
    const [_clips, clipboardRef] = useFirebaseValue(firebasePath || "")

    // Track subscribers for real-time updates
    const subscribersRef = useRef<Set<(clips: Clip[]) => void>>(new Set())

    // Notify all subscribers when clips change
    useEffect(() => {
      const currentClips = Array.isArray(_clips) ? _clips : []
      subscribersRef.current.forEach((callback) => {
        callback(currentClips)
      })
    }, [_clips])

    return useMemo(
      () => ({
        isAvailable: () => {
          return clipboardRef !== null
        },

        subscribeToClips: (callback: (clips: Clip[]) => void) => {
          // Initial callback with current clips
          const currentClips = Array.isArray(_clips) ? _clips : []
          callback(currentClips)

          // Add to subscribers
          subscribersRef.current.add(callback)

          // Return unsubscribe function
          return () => {
            subscribersRef.current.delete(callback)
          }
        },

        saveClips: async (clips: Clip[]): Promise<void> => {
          // There are very rare cases where the user is authenticated in Pathwright
          // but the user has no clipboardRef (it is null).
          // The following try/catch + Sentry logging is an attempt to debug this issue.
          if (!clipboardRef) {
            // Attempt to log user's fb token data to Sentry.
            try {
              const { currentUser } = getAuth(app)
              const accessToken = (currentUser as any)?.accessToken
              let tokenData: any = null

              if (accessToken && typeof accessToken === "string") {
                try {
                  tokenData = jwtDecode(accessToken)
                } catch {
                  tokenData = null
                }
              }

              const issue = {
                message: "Failed attempt to copy item to clipboard.",
                extra: ["clipboard:user_fb_token_data", tokenData]
              }

              withScope<void>((scope) => {
                scope.setExtra(issue.extra[0], issue.extra[1])
                captureMessage(issue.message)
                console.warn(issue.message, issue.extra)
              })

              // Attempt to refresh the user's token in Sentry.
              if (currentUser) {
                const token = await getAuth(app).currentUser?.getIdToken()
                let refreshedTokenData: any = null

                if (token && typeof token === "string") {
                  try {
                    refreshedTokenData = jwtDecode(token)
                  } catch {
                    refreshedTokenData = null
                  }
                }

                const issue = {
                  message: "Attempting to refresh user's firebase auth token.",
                  extra: [
                    "clipboard:user_refreshed_fb_token_data",
                    refreshedTokenData
                  ]
                }

                withScope<void>((scope) => {
                  scope.setExtra(issue.extra[0], issue.extra[1])
                  captureMessage(issue.message)
                  console.warn(issue.message, issue.extra)
                })
              }
            } catch (error) {
              captureException(error)
            }
            // Silently fail, ignoring user's attempt to copy to clipboard.
            return
          }
          await set(clipboardRef, clips)
        },

        getName: () => "FirebaseBackend"
      }),
      [me, app, firebasePath, clipboardRef]
    )
  } catch (error) {
    // Log the error for debugging
    console.warn("[Clipboard] Firebase backend failed to initialize:", error)

    // Return a backend that is always unavailable
    return {
      isAvailable: () => false,
      subscribeToClips: (callback: (clips: Clip[]) => void) => {
        // Return empty clips and no-op unsubscribe
        callback([])
        return () => {}
      },
      saveClips: async (clips: Clip[]): Promise<void> => {
        // Silently fail - this backend is not available
        console.warn(
          "[Clipboard] Firebase backend is unavailable, save operation ignored"
        )
      },
      getName: () => "FirebaseBackend (Unavailable)"
    }
  }
}

export default useFirebaseBackend
